import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  IconArrowLeft, IconBook, IconClock, IconCheck, IconSparkles,
  IconLayoutGrid, IconRoute, IconChartBar, IconPlus, IconWand,
  IconLock, IconPencil, IconShoppingCart, IconAward, IconTrash,
} from "@tabler/icons-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal";

const LEVEL_ES = { intro: "Introducción", intermedio: "Intermedio", avanzado: "Avanzado", capsula: "Cápsula" };

export default function AulaHome() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("catalogo");
  const isAdmin = user?.is_admin || user?.role === "psicologa";

  const tabs = [
    { id: "catalogo", label_es: "Catálogo", label_en: "Catalog", icon: <IconLayoutGrid size={13} /> },
    { id: "recomendadas", label_es: "Para ti", label_en: "For you", icon: <IconSparkles size={13} /> },
    { id: "ruta", label_es: "Cápsulas", label_en: "Capsules", icon: <IconRoute size={13} /> },
    ...(isAdmin ? [
      { id: "vivo", label_es: "Currículo vivo", label_en: "Living curriculum", icon: <IconChartBar size={13} /> },
      { id: "seguimiento", label_es: "Seguimiento", label_en: "Tracking", icon: <IconChartBar size={13} /> },
    ] : []),
  ];

  return (
    <>
      <div className="subbar">
        <button className="btn-ghost btn text-[12px] px-2 py-1" onClick={() => nav("/app")} data-testid="aula-back">
          <IconArrowLeft size={13} /> {t("app.back")}
        </button>
        <span className="t">MÓDULO AULA</span>
      </div>
      <div className="wrap-app max-w-[1080px] py-10">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <p className="eyebrow">{lang === "es" ? "Formación aplicada" : "Applied training"}</p>
            <h1 className="font-display font-medium text-[clamp(24px,3vw,32px)] mt-1">
              {lang === "es" ? "Aula del instituto" : "Institute Aula"}
            </h1>
            <p className="text-muted text-[13.5px] mt-1">
              {lang === "es"
                ? "Cursos y cápsulas hechos con evidencia. Aplicables mañana en el aula."
                : "Courses and capsules built with evidence. Applicable tomorrow."}
            </p>
          </div>
          {isAdmin && (
            <button className="btn" onClick={() => nav("/app/aula/nuevo")} data-testid="aula-new-course">
              <IconPlus size={13} /> {lang === "es" ? "Nuevo curso" : "New course"}
            </button>
          )}
        </div>

        <div className="border-b border-line flex gap-1 overflow-x-auto">
          {tabs.map((tt) => (
            <button
              key={tt.id}
              onClick={() => setTab(tt.id)}
              className={`px-3 py-2 text-[12.5px] font-medium transition border-b-2 -mb-px flex items-center gap-1.5 ${
                tab === tt.id ? "border-teal text-teal-deep" : "border-transparent text-muted hover:text-teal-deep"
              }`}
              data-testid={`aula-tab-${tt.id}`}
            >
              {tt.icon} {lang === "es" ? tt.label_es : tt.label_en}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "catalogo" && <Catalog user={user} />}
          {tab === "recomendadas" && <Recommended user={user} />}
          {tab === "ruta" && <Capsules user={user} />}
          {tab === "vivo" && isAdmin && <LivingCurriculum />}
          {tab === "seguimiento" && isAdmin && <Tracking />}
        </div>
      </div>
    </>
  );
}

function CourseCard({ item, onOpen, matchedSignals, reason, isAdmin, onEdit, onDelete }) {
  const { lang } = useI18n();
  const c = item.course || item;
  const pct = c.progress_pct || 0;
  const price = Number(c.price || 0);
  const isFree = c.is_free || price === 0;
  const hasAccess = c.has_access !== false; // if undefined, assume yes (compat)
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mod-card relative"
      onClick={() => onOpen(c.id)}
      data-testid={`aula-course-${c.id}`}
    >
      {isAdmin && (
        <div className="absolute top-3 right-3 flex items-center gap-1">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit && onEdit(c.id); }}
            className="text-tert hover:text-teal-deep p-1"
            title={lang === "es" ? "Editar" : "Edit"}
            aria-label={lang === "es" ? "Editar curso" : "Edit course"}
            data-testid={`aula-edit-${c.id}`}
          >
            <IconPencil size={14} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete && onDelete(c); }}
            className="text-tert hover:text-errtext p-1"
            title={lang === "es" ? "Eliminar" : "Delete"}
            aria-label={lang === "es" ? "Eliminar curso" : "Delete course"}
            data-testid={`aula-delete-${c.id}`}
          >
            <IconTrash size={14} />
          </button>
        </div>
      )}
      <div className="flex items-center gap-2 mb-2">
        <span className="tag">{c.track === "docente" ? "Docente" : c.track === "sombra" ? "Sombra" : c.track === "familia" ? "Familia" : c.track === "escuela" ? "Escuela" : "Ambos"}</span>
        <span className="tag tag-honey">{LEVEL_ES[c.level] || c.level}</span>
        {c.is_capsule && <span className="tag" style={{ background: "#EEEDFE", color: "#3C3489" }}>{lang === "es" ? "Cápsula" : "Capsule"}</span>}
        {!hasAccess && <span className="tag" style={{ background: "#F3EDE1", color: "#854F0B" }}><IconLock size={10} className="inline mr-0.5" /> {lang === "es" ? "Bloqueado" : "Locked"}</span>}
      </div>
      <h3 className="font-display text-[18px] font-medium">{c.title}</h3>
      {c.subtitle && <p className="text-tert text-[12.5px] mt-1">{c.subtitle}</p>}
      <p className="text-muted text-[13px] mt-2 leading-relaxed line-clamp-3">{c.description}</p>
      <div className="flex items-center gap-3 mt-3 text-tert text-[11.5px]">
        <span className="flex items-center gap-1"><IconClock size={12} /> {c.duration_min || "—"} min</span>
        <span>· {c.total_lessons || 0} {lang === "es" ? "lecciones" : "lessons"}</span>
      </div>
      <div className="flex items-center justify-between mt-3">
        {isFree ? (
          <span className="tag" style={{ background: "#E7F3EE", color: "#0A5C3F" }}>{lang === "es" ? "Gratuito" : "Free"}</span>
        ) : (
          <span className="font-display text-[18px] font-medium text-teal-deep">${price.toFixed(2)}</span>
        )}
        {!hasAccess && !isFree && (
          <span className="text-honey text-[12px] font-medium flex items-center gap-1">
            <IconShoppingCart size={12} /> {lang === "es" ? "Comprar" : "Buy"}
          </span>
        )}
      </div>
      {pct > 0 && pct < 100 && (
        <>
          <div className="track mt-3"><div className="fill" style={{ width: `${pct}%` }} /></div>
          <p className="text-teal text-[11.5px] mt-1">{pct}% {lang === "es" ? "completo" : "complete"}</p>
        </>
      )}
      {pct === 100 && (
        <div className="mt-3 flex items-center gap-2 rounded-lg bg-honey/15 border border-honey/40 p-2" data-testid={`aula-cert-badge-${c.id}`}>
          <IconAward size={16} className="text-honey shrink-0" />
          <p className="text-[12.5px] font-medium text-teal-deep">
            {lang === "es" ? "Certificado disponible" : "Certificate available"}
          </p>
        </div>
      )}
      {reason && (
        <div className="mt-3 pt-3 border-t border-line text-[12px] text-teal-deep flex items-start gap-1.5">
          <IconSparkles size={13} className="mt-0.5 text-honey" />
          <span>{reason}</span>
        </div>
      )}
      {matchedSignals && matchedSignals.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {matchedSignals.map((s) => <span key={s} className="chip text-[11px] px-1.5 py-0.5">{s}</span>)}
        </div>
      )}
    </motion.div>
  );
}

function Catalog({ user }) {
  const { lang } = useI18n();
  const nav = useNavigate();
  const isAdmin = user?.is_admin || user?.role === "psicologa";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadItems = async () => {
    setLoading(true);
    try {
      const track = user?.role === "docente" ? "docente" : user?.role === "sombra" ? "sombra" : user?.role === "familia" ? "familia" : "todas";
      const { data } = await api.get(`/aula/courses?track=${track}`);
      setItems(data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadItems(); }, [user]);

  if (loading) return <p className="text-tert text-[12px] font-mono uppercase">Cargando…</p>;
  if (error) return <p className="text-errtext text-[13px]">{error}</p>;
  if (items.length === 0) return <p className="text-tert text-[13px]">{lang === "es" ? "Aún no hay cursos para ti." : "No courses yet."}</p>;

  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((c) => (
          <CourseCard
            key={c.id}
            item={c}
            onOpen={(id) => nav(`/app/aula/${id}`)}
            isAdmin={isAdmin}
            onEdit={(id) => nav(`/app/aula/edit/${id}`)}
            onDelete={(course) => setDeleteTarget(course)}
          />
        ))}
      </div>
      <ConfirmDeleteModal
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={async () => {
          await api.delete(`/aula/courses/${deleteTarget.id}`);
          setDeleteTarget(null);
          await loadItems();
        }}
        title={lang === "es" ? "Eliminar curso" : "Delete course"}
        description={
          deleteTarget
            ? (lang === "es"
                ? `Se eliminará el curso <b>${deleteTarget.title}</b> junto con sus módulos y lecciones. Los progresos de estudiantes también se pierden. Esta acción es irreversible.`
                : `Course <b>${deleteTarget.title}</b> and all modules/lessons will be removed. Student progress will also be lost. This is irreversible.`)
            : ""
        }
        testId="aula-course-delete-modal"
      />
    </>
  );
}

function Recommended() {
  const { lang } = useI18n();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/aula/recommendations");
        setData(data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <p className="text-tert text-[12px] font-mono uppercase">Cargando…</p>;
  const recs = data?.recommendations || [];

  return (
    <div>
      <div className="card mb-5 bg-teal-soft/50 border-teal-soft">
        <div className="flex items-center gap-2 text-teal-deep">
          <IconWand size={16} />
          <span className="font-medium text-[13px]">
            {lang === "es" ? "Recomendaciones del Sistema Inteligente" : "Smart System recommendations"}
          </span>
        </div>
        <p className="text-teal-deep/80 text-[13px] mt-2">
          {lang === "es"
            ? `Basadas en los patrones observados en tus niños/alumnos durante los últimos ${data?.week_days || 7} días.`
            : `Based on patterns in your children/students in the last ${data?.week_days || 7} days.`}
        </p>
        {data?.signals && Object.keys(data.signals).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {Object.entries(data.signals).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([tag, count]) => (
              <span key={tag} className="chip text-[11px] px-2 py-0.5">{tag} · {count}</span>
            ))}
          </div>
        )}
      </div>

      {recs.length === 0 ? (
        <p className="text-tert text-[13px]">
          {lang === "es"
            ? "Aún no hay suficientes señales en tu red para recomendar. Registra más partes del día y vuelve."
            : "Not enough signals yet."}
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {recs.map((r) => (
            <CourseCard
              key={r.course.id}
              item={r.course}
              onOpen={(id) => nav(`/app/aula/${id}`)}
              matchedSignals={r.matched_signals}
              reason={r.reason}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Capsules() {
  const { lang } = useI18n();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  useEffect(() => { (async () => {
    const { data } = await api.get("/aula/courses?only_capsules=true");
    setItems(data);
  })(); }, []);
  return items.length === 0 ? (
    <p className="text-tert text-[13px]">{lang === "es" ? "Sin cápsulas aún." : "No capsules yet."}</p>
  ) : (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {items.map((c) => <CourseCard key={c.id} item={c} onOpen={(id) => nav(`/app/aula/${id}`)} />)}
    </div>
  );
}

function LivingCurriculum() {
  const { lang } = useI18n();
  const [data, setData] = useState(null);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/aula/insights/vivo"); setData(data); }
    catch { /* ignore */ }
  })(); }, []);
  if (!data) return <p className="text-tert text-[12px] font-mono uppercase">Cargando…</p>;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-3">
        <div className="kpi"><div className="kl">{lang === "es" ? "Señales / 14d" : "Signals / 14d"}</div><div className="kv">{data.total_signals}</div></div>
        <div className="kpi"><div className="kl">{lang === "es" ? "Temas top" : "Top topics"}</div><div className="kv">{data.top_signals?.length || 0}</div></div>
        <div className="kpi"><div className="kl">{lang === "es" ? "Vacíos de contenido" : "Content gaps"}</div><div className="kv" style={{ color: "#854f0b" }}>{data.content_gaps?.length || 0}</div></div>
      </div>
      <div className="card">
        <p className="lbl m-0 mb-3">{lang === "es" ? "Top señales de la red" : "Top network signals"}</p>
        <div className="space-y-2">
          {(data.top_signals || []).map((s) => (
            <div key={s.tag} className="flex items-center gap-3">
              <span className="tag min-w-[100px] text-center">{s.tag}</span>
              <div className="track flex-1"><div className="fill" style={{ width: `${Math.min(100, s.count * 8)}%` }} /></div>
              <span className="font-mono text-[11px] text-tert w-8 text-right">{s.count}</span>
            </div>
          ))}
        </div>
      </div>
      {(data.content_gaps || []).length > 0 && (
        <div className="card border-l-[3px] border-l-honey">
          <p className="lbl m-0 mb-2 text-honey" style={{ color: "#854f0b" }}>
            {lang === "es" ? "Oportunidades de curso" : "Course opportunities"}
          </p>
          <p className="text-[13.5px] text-muted mb-2">
            {lang === "es"
              ? "Estas señales aparecen en la red pero no tienen un curso que las atienda:"
              : "Signals present in the network but with no course covering them:"}
          </p>
          <div className="flex flex-wrap gap-2">
            {data.content_gaps.map((g) => <span key={g} className="chip">{g}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}

function Tracking() {
  const { lang } = useI18n();
  const [rows, setRows] = useState([]);
  const [certStats, setCertStats] = useState([]);
  const [sombraQueue, setSombraQueue] = useState([]);
  const [approving, setApproving] = useState(null);

  const loadAll = React.useCallback(async () => {
    try {
      const [{ data: r }, { data: cs }, { data: q }] = await Promise.all([
        api.get("/aula/admin/tracking"),
        api.get("/aula/admin/certificates-summary"),
        api.get("/aula/admin/sombra-approvals?status=pending_interview"),
      ]);
      setRows(r); setCertStats(cs); setSombraQueue(q);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const approve = async (id) => {
    setApproving(id + "-approve");
    try { await api.post(`/aula/admin/sombra-approvals/${id}/approve`); await loadAll(); }
    catch { /* ignore */ }
    finally { setApproving(null); }
  };
  const reject = async (id) => {
    if (!window.confirm(lang === "es" ? "¿Rechazar esta solicitud? La maestra deberá reintentar." : "Reject this request?")) return;
    setApproving(id + "-reject");
    try { await api.post(`/aula/admin/sombra-approvals/${id}/reject`); await loadAll(); }
    catch { /* ignore */ }
    finally { setApproving(null); }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString(lang === "es" ? "es-HN" : "en-US", { day: "numeric", month: "short", year: "numeric" }) : "—";
  return (
    <div className="space-y-5">
      {sombraQueue.length > 0 && (
        <div className="card border-l-[4px] border-l-honey" data-testid="sombra-approval-queue">
          <div className="flex items-center gap-2 mb-3">
            <IconAward size={16} className="text-honey" />
            <p className="lbl m-0">{lang === "es" ? "Entrevistas pendientes · Certificado Maestra Sombra" : "Pending interviews · Shadow Teacher certificate"}</p>
          </div>
          <p className="text-tert text-[12.5px] mb-3">
            {lang === "es"
              ? "Estas maestras completaron el curso al 100% y esperan tu entrevista antes de recibir el certificado."
              : "These teachers reached 100% and are waiting for your interview before receiving the certificate."}
          </p>
          <ul className="space-y-2">
            {sombraQueue.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-3 border-t border-line pt-2 flex-wrap" data-testid={`sombra-approval-${a.id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-[14px]">{a.user_name || a.user_email}</p>
                  <p className="text-tert text-[11.5px]">{a.user_email} · {a.course_title} · {lang === "es" ? "solicitado" : "requested"} {fmt(a.requested_at)}</p>
                </div>
                <div className="flex gap-1.5">
                  <button className="btn text-[11.5px]" onClick={() => approve(a.id)} disabled={approving === a.id + "-approve"} data-testid={`sombra-approve-${a.id}`}>
                    {approving === a.id + "-approve" ? "…" : (lang === "es" ? "Aprobar" : "Approve")}
                  </button>
                  <button className="btn-ghost btn text-[11.5px]" onClick={() => reject(a.id)} disabled={approving === a.id + "-reject"} data-testid={`sombra-reject-${a.id}`}>
                    {lang === "es" ? "Rechazar" : "Reject"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="card" data-testid="aula-cert-summary">
        <div className="flex items-center gap-2 mb-3">
          <IconAward size={16} className="text-honey" />
          <p className="lbl m-0">{lang === "es" ? "Certificados emitidos" : "Certificates issued"}</p>
        </div>
        {certStats.length === 0 ? (
          <p className="text-tert text-[12.5px]">{lang === "es" ? "Aún no se han descargado certificados." : "No certificates downloaded yet."}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-tert font-mono text-[11px] uppercase tracking-widest">
                  <th className="text-left py-2">{lang === "es" ? "Curso" : "Course"}</th>
                  <th className="text-left py-2">{lang === "es" ? "Track" : "Track"}</th>
                  <th className="text-right py-2">{lang === "es" ? "Alumnos certificados" : "Certified"}</th>
                  <th className="text-right py-2">{lang === "es" ? "Descargas totales" : "Downloads"}</th>
                  <th className="text-right py-2">{lang === "es" ? "Último" : "Last"}</th>
                </tr>
              </thead>
              <tbody>
                {certStats.map((s) => (
                  <tr key={s.course_id} className="border-t border-line" data-testid={`aula-cert-row-${s.course_id}`}>
                    <td className="py-2 font-medium">{s.course_title || s.course_id}</td>
                    <td className="py-2 text-muted">{s.course_track || "—"}</td>
                    <td className="py-2 text-right">{s.issued_count}</td>
                    <td className="py-2 text-right font-medium text-honey">{s.total_downloads}</td>
                    <td className="py-2 text-right text-tert text-[12px]">{fmt(s.last_issued_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="card overflow-x-auto" data-testid="aula-tracking-table">
        <p className="lbl m-0 mb-3">{lang === "es" ? "Progreso por usuario" : "User progress"}</p>
        <table className="w-full text-[13.5px]">
          <thead>
            <tr className="text-tert font-mono text-[11px] uppercase tracking-widest">
              <th className="text-left py-2">{lang === "es" ? "Nombre" : "Name"}</th>
              <th className="text-left py-2">{lang === "es" ? "Rol" : "Role"}</th>
              <th className="text-right py-2">{lang === "es" ? "Iniciados" : "Started"}</th>
              <th className="text-right py-2">{lang === "es" ? "Completados" : "Completed"}</th>
              <th className="text-right py-2">{lang === "es" ? "Promedio" : "Avg"}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.user_id} className="border-t border-line">
                <td className="py-2 font-medium">{r.name}</td>
                <td className="py-2">{r.role}</td>
                <td className="py-2 text-right">{r.courses_started}</td>
                <td className="py-2 text-right">{r.courses_completed}</td>
                <td className="py-2 text-right font-medium">{r.avg_progress}%</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan="5" className="py-4 text-tert text-center">{lang === "es" ? "Sin datos aún." : "No data yet."}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
